import { useState } from 'react';
import { ProfileCard, type SequenceLogEntry } from '@components/ProfileCard';
import { EnrichmentJsonModal } from '@components/EnrichmentJsonModal';
import styles from '../DemoPage.module.scss';
import { SectionHeader } from './shared';

// Sample sequence log data
const sampleSequenceLog: SequenceLogEntry[] = [
  { sendTime: 'Jan 21, 09:00 AM', status: 'Completed', subject: 'A Voice & Chat Agent for your business' },
  { sendTime: 'Jan 23, 10:30 AM', status: 'Completed', subject: 'Re: A Voice & Chat Agent for your business' },
  { sendTime: 'Jan 25, 09:00 AM', status: 'Scheduled', subject: 'Keeping your custom demo live for 30 days' },
];

// Sample Apollo enrichment data for demo
const sampleEnrichmentData: Record<string, unknown> = {
  id: 'abc123',
  first_name: 'Anjana',
  last_name: 'Sijann',
  name: 'Anjana Sijann',
  linkedin_url: 'https://linkedin.com/in/anjana-sijann',
  title: 'Associate Product Photographer and Graphic Designer',
  email_status: 'verified',
  photo_url: 'https://example.com/photo.jpg',
  email: 'anjana@company.com',
  organization_name: 'Acme Corp',
  organization: {
    id: 'org456',
    name: 'Acme Corp',
    website_url: 'https://acmecorp.com',
    industry: 'Marketing & Advertising',
    estimated_num_employees: 250,
    city: 'San Francisco',
    state: 'California',
    country: 'United States',
    founded_year: 2015,
  },
  seniority: 'entry',
  departments: ['Design', 'Marketing'],
  employment_history: [
    {
      organization_name: 'Acme Corp',
      title: 'Associate Product Photographer',
      start_date: '2022-03',
      current: true,
    },
    {
      organization_name: 'Previous Co',
      title: 'Junior Designer',
      start_date: '2020-01',
      end_date: '2022-02',
      current: false,
    },
  ],
  city: 'San Francisco',
  state: 'California',
  country: 'United States',
};

export const ProfileCardSection = () => {
  const [jsonModal, setJsonModal] = useState<{
    name: string;
    data: Record<string, unknown>;
  } | null>(null);

  return (
    <section className={styles.section}>
      <SectionHeader
        title="Profile Card"
        description="A card component displaying profile information with avatar, name, job title, and email. Supports enriched/contacted states with action buttons and contact status tracking."
        figmaNodeId="361-3974"
        styles={styles}
      />

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Not Enriched (Default)</h3>
        <p className={styles.hint}>Shows masked name and &quot;Enrich&quot; button in footer.</p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Anjana Si***n"
            jobTitle="Associate Product Photographer and Graphic Designer"
            onEnrich={() => {}}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Enriched, Not Contacted</h3>
        <p className={styles.hint}>
          Shows email, &quot;Reach Out&quot; button, and optional LinkedIn.
        </p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Anjana Si***n"
            jobTitle="Associate Product Photographer and Graphic Designer"
            email="someone@company.com"
            enriched
            onReachOut={() => {}}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Enriched with LinkedIn</h3>
        <p className={styles.hint}>Enriched profile with LinkedIn button visible.</p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Anjana Si***n"
            jobTitle="Associate Product Photographer and Graphic Designer"
            email="someone@company.com"
            enriched
            hasLinkedIn
            linkedInUrl="https://linkedin.com/in/example"
            onReachOut={() => {}}
            onLinkedInClick={() => {}}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Enriched with Verified Email</h3>
        <p className={styles.hint}>Email shows verified badge icon.</p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Anjana Si***n"
            jobTitle="Associate Product Photographer and Graphic Designer"
            email="verified@company.com"
            enriched
            emailVerified
            hasLinkedIn
            linkedInUrl="https://linkedin.com/in/example"
            onReachOut={() => {}}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Enriched with View JSON (Info Icon)</h3>
        <p className={styles.hint}>
          Enriched profile with info icon button. Click the info icon to open the enrichment JSON
          modal. Compare info icon size with LinkedIn icon.
        </p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Anjana Si***n"
            jobTitle="Associate Product Photographer and Graphic Designer"
            email="anjana@company.com"
            enriched
            emailVerified
            hasLinkedIn
            linkedInUrl="https://linkedin.com/in/example"
            onReachOut={() => {}}
            onViewJson={() =>
              setJsonModal({ name: 'Anjana Sijann', data: sampleEnrichmentData })
            }
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Enriched with View JSON Only (No LinkedIn)</h3>
        <p className={styles.hint}>
          Info icon without LinkedIn button — tests alignment when only one action icon is present.
        </p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="John D***e"
            jobTitle="Senior Developer"
            email="john@company.com"
            enriched
            onReachOut={() => {}}
            onViewJson={() =>
              setJsonModal({ name: 'John Doe', data: sampleEnrichmentData })
            }
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Contacted - In Progress</h3>
        <p className={styles.hint}>
          White background, unmasked name, and contact status button. Click status to see Sequence Log
          popover.
        </p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Anjana Si***n"
            enrichedName="Anjana Sijann"
            jobTitle="Associate Product Photographer and Graphic Designer"
            email="someone@company.com"
            enriched
            contacted
            hasLinkedIn
            linkedInUrl="https://linkedin.com/in/example"
            emailVerified
            contactStatus="in-progress"
            contactProgress={{ current: 1, total: 3 }}
            sequenceLog={sampleSequenceLog}
            onStatusClick={() => {}}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Contacted - Completed</h3>
        <p className={styles.hint}>Status shows as completed with grey color.</p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="John D***e"
            enrichedName="John Doe"
            jobTitle="Senior Developer"
            email="john@company.com"
            enriched
            contacted
            contactStatus="completed"
            contactProgress={{ current: 3, total: 3 }}
            sequenceLog={sampleSequenceLog}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>Contacted - Successful</h3>
        <p className={styles.hint}>Status shows as successful with green color.</p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Jane S***h"
            enrichedName="Jane Smith"
            jobTitle="Product Manager"
            email="jane@company.com"
            enriched
            contacted
            emailVerified
            contactStatus="successful"
            contactProgress={{ current: 2, total: 2 }}
            sequenceLog={sampleSequenceLog}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>With Avatar Image</h3>
        <p className={styles.hint}>Custom avatar using avatarSrc prop.</p>
        <div className={styles.profileCardContainer}>
          <ProfileCard
            name="Alex Johnson"
            avatarSrc="https://i.pravatar.cc/32?img=3"
            jobTitle="Senior Developer"
            enriched
            email="alex@company.com"
            onReachOut={() => {}}
          />
        </div>
      </div>

      <div className={styles.subsection}>
        <h3 className={styles.subsectionTitle}>State Comparison</h3>
        <p className={styles.hint}>Side-by-side comparison of all states.</p>
        <div className={styles.profileCardGrid}>
          <div>
            <h4 className={styles.cardLabel}>Not Enriched</h4>
            <ProfileCard name="User A***a" jobTitle="Designer" onEnrich={() => {}} />
          </div>
          <div>
            <h4 className={styles.cardLabel}>Enriched</h4>
            <ProfileCard
              name="User A***a"
              jobTitle="Designer"
              email="user@company.com"
              enriched
              onReachOut={() => {}}
            />
          </div>
          <div>
            <h4 className={styles.cardLabel}>Contacted</h4>
            <ProfileCard
              name="User A***a"
              enrichedName="User Alpha"
              jobTitle="Designer"
              email="user@company.com"
              enriched
              contacted
              contactStatus="in-progress"
              contactProgress={{ current: 1, total: 2 }}
              sequenceLog={sampleSequenceLog}
            />
          </div>
        </div>
      </div>

      {/* Enrichment JSON Modal */}
      <EnrichmentJsonModal
        isOpen={!!jsonModal}
        onClose={() => setJsonModal(null)}
        personName={jsonModal?.name ?? ''}
        data={jsonModal?.data ?? {}}
      />
    </section>
  );
};
